/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  BuilderOfficeData,
  GraphicalTakeOffTopRestService,
  GtoStyle,
  GtoUom,
  OfficeMeasurement,
  TakeOffParamsGridFilter,
  TakeOffParamsRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, InitDialogViewService, InitViewService } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SlicedDataSource } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';
import { assign, flatten, includes } from 'lodash';
import { Observable } from 'rxjs';
import { GraphicalTakeOffTop, TakeOffParam, TakeOffParamsCheckUsage } from './estimating-take-off-params-tab.model';

export interface EstimatingTakeOffParamDetailsDialogConfig {
  id?: ID;
}

@Injectable()
export class EstimatingTakeOffParamsTabBusinessService implements InitViewService, InitDialogViewService<EstimatingTakeOffParamDetailsDialogConfig> {

  constructor(private restService: TakeOffParamsRestService,
              private topRestService: GraphicalTakeOffTopRestService,
              private userContext: BuilderUserContext,
              private gridLayoutService: GridLayoutService,
              private dictionaryService: DictionaryService) {
  }

  /**
   * @returns {{gridLayout: Observable<GridLayout>}}
   */
  initView(): any {
    return {
      gridLayout: this.gridLayoutService.get('takeOffParams'),
      gtoUoms: this.dictionaryService.getByEnum(GtoUom),
      gtoStyles: this.dictionaryService.getByEnum(GtoStyle),
    };
  }

  /**
   * @param route
   * @returns {*} Init Values
   */
  initDialogView(route: DialogRouteSnapshot<EstimatingTakeOffParamDetailsDialogConfig>): any {
    const excludedMetrics = (this.userContext.office
      && (<BuilderOfficeData>this.userContext.office).measurementScale === OfficeMeasurement.Imperial)
      ? [GtoUom.Millimeters, GtoUom.MetersSquare, GtoUom.Meters]
      : [GtoUom.Feets, GtoUom.FeetsSquare];

    return {
      gtoUoms: this.dictionaryService.getByEnum(GtoUom).pipe(
        filter$((gtoUom: any) => !includes(flatten([excludedMetrics]), gtoUom.id))),
      gtoStyles: this.dictionaryService.getByEnum(GtoStyle),
      takeOffParam: route.input.id ? this.get(<ID>route.input.id) : {},
    };
  }

  /**
   * Creates data source for list in Grid Component
   * @param filter
   * @returns {SlicedDataSource<TakeOffParam>}
   */
  findAllAsDataSource(filter: TakeOffParamsGridFilter): SlicedDataSource<TakeOffParam> {
    return this.restService.findAllAsDataSource(filter);
  }

  /**
   * Checks if ToP is in usage and returns { delete, version }
   * { delete } - true->NotUsed | false->Used
   * @param takeOffParam
   * @returns {Observable<TakeOffParamsCheckUsage>}
   */
  checkUsage(takeOffParam: TakeOffParam): Observable<TakeOffParamsCheckUsage> {
    return this.restService.checkUsage(takeOffParam);
  }

  /**
   * Removes or marks ToP as deativated
   * @param takeOffParam
   * @param {TakeOffParamsCheckUsage} takeOffParamCheckUsage
   * @returns {Observable<TakeOffParamDto>}
   */
  deactivateOrDelete(takeOffParam: TakeOffParam, takeOffParamCheckUsage: TakeOffParamsCheckUsage): Observable<any> {
    return this.restService.deactivate(takeOffParam, takeOffParamCheckUsage);
  }

  /**
   * Marks ToP as active
   * @param takeOffParam
   * @returns {Observable<TakeOffParamDto>}
   */
  activate(takeOffParam: TakeOffParam): Observable<TakeOffParam> {
    return this.restService.activate(takeOffParam);
  }

  /**
   * @param id
   * @returns {Observable<TakeOffParamDto>}
   */
  get(id: ID): Observable<TakeOffParam> {
    return this.restService.get(id);
  }

  /**
   * Updates of create new Take Off Param
   * @param takeOffParam
   * @returns {any}
   */
  save(takeOffParam: TakeOffParam): Observable<TakeOffParam> {
    if (takeOffParam.takeOffParamId) {
      return this.restService.update(takeOffParam);
    }
    return this.restService.create(assign(takeOffParam, { version: 0 }));
  }

  /**
   * Create TOP for GTO
   *
   * @param {ID} hpId
   * @param {GraphicalTakeOffTop} takeOffParam
   * @param {string} entryPoint
   * @returns {Observable<GraphicalTakeOffTop>}
   */
  saveForGTO(hpId: ID, takeOffParam: TakeOffParam, entryPoint: string): Observable<GraphicalTakeOffTop> {
    return this.topRestService.create(hpId, assign(takeOffParam, { version: 0 }), entryPoint);
  }

  bulkDelete(items: any[]): Observable<any> {
    return this.topRestService.bulkDelete(items);
  }
}
